import { ChangeDetectionStrategy, Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { connectState } from '@examdojo/angular/util';
import { ButtonComponent } from '@examdojo/core/button';
import { createTranslatedOptions } from '@examdojo/core/i18n';
import { IconComponent } from '@examdojo/core/icon';
import { IfSmallDirective, IfWideLayoutDirective } from '@examdojo/layout';
import { TranslocoPipe } from '@jsverse/transloco';
import { combineLatest, map, merge, tap } from 'rxjs';
import {
  QUESTION_BANK_SORT_OPTION_TO_LABEL_KEY,
  QUESTION_BANK_SORT_OPTIONS,
  QuestionBankSortOption,
} from '../../app/question-bank.model';
import { QuestionBankQuery } from '../../app/question-bank.query';
import { QuestionBankService } from '../../app/question-bank.service';

@Component({
  selector: 'dojo-question-bank-sort',
  templateUrl: './question-bank-sort.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      .sort-button-trigger ::ng-deep {
        &::part(native) {
          transform: none;
        }
      }
    `,
  ],
  imports: [
    IconComponent,
    ButtonComponent,
    ReactiveFormsModule,
    TranslocoPipe,
    MatMenuModule,
    IfSmallDirective,
    IfWideLayoutDirective,
  ],
})
export class QuestionBankSortComponent {
  constructor(
    private readonly questionBankQuery: QuestionBankQuery,
    private readonly questionBankService: QuestionBankService,
  ) {
    merge(this.syncSortCtrlWithStore(), this.syncStoreWithSortCtrl()).pipe(takeUntilDestroyed()).subscribe();
  }

  readonly sortCtrl = new FormControl<QuestionBankSortOption>(QuestionBankSortOption.MostRelevant, {
    nonNullable: true,
  });

  readonly sortOptions$ = createTranslatedOptions(QUESTION_BANK_SORT_OPTIONS, QUESTION_BANK_SORT_OPTION_TO_LABEL_KEY);

  readonly currentSortText$ = combineLatest([this.questionBankQuery.selectProp('sortBy'), this.sortOptions$]).pipe(
    map(([currentValue, sortOptions]) => {
      const currentOption = sortOptions.find((option) => option.value === currentValue);
      return currentOption?.label || '';
    }),
  );

  readonly state = connectState({
    sortOptions: this.sortOptions$,
    currentSortText: this.currentSortText$,
  });

  selectSortOption(option: QuestionBankSortOption): void {
    this.sortCtrl.setValue(option);
  }

  private syncSortCtrlWithStore() {
    return this.questionBankQuery.selectProp('sortBy').pipe(
      tap((sortBy) => {
        if (this.sortCtrl.value !== sortBy) {
          this.sortCtrl.setValue(sortBy, { emitEvent: false });
        }
      }),
    );
  }

  private syncStoreWithSortCtrl() {
    return this.sortCtrl.valueChanges.pipe(tap((sortBy) => this.questionBankService.changeSort(sortBy)));
  }
}
